<template>
  <div class="tvm" dir="rtl">
    <div class="tvm__header">
      <div class="tvm__bar">
        <div class="tvm__bar-actions">
          <button type="button" class="tvm__bar-btn" :aria-label="$t('print')" @click="list.openPrintStudio()">
            <v-icon size="20" color="#5B7C99">mdi-printer-outline</v-icon>
          </button>
          <button type="button" class="tvm__bar-btn" :aria-label="$t('refresh')" @click="onRefreshTap">
            <v-icon size="20" color="#37474F">mdi-refresh</v-icon>
          </button>
        </div>
        <h1 class="tvm__title">
          {{ $t("turnover") }}
          <small v-if="list.turnoverAccountName">{{ list.turnoverAccountName }}</small>
        </h1>
        <button type="button" class="tvm__bar-btn tvm__bar-btn--back" :aria-label="$t('back')" @click="goBack">
          <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
        </button>
      </div>

      <div class="tvm__search-row">
        <div class="tvm__search">
          <v-icon size="20" color="#90A4AE" class="tvm__search-icon">mdi-magnify</v-icon>
          <input
            v-model="list.search"
            type="search"
            class="tvm__search-input"
            :placeholder="$t('search')"
            autocomplete="off"
          />
          <button
            v-if="list.search"
            type="button"
            class="tvm__search-clear"
            :aria-label="$t('clear')"
            @click="list.search = ''"
          >
            <v-icon size="16" color="#90A4AE">mdi-close</v-icon>
          </button>
        </div>
        <button
          type="button"
          class="tvm__filter-btn"
          :class="{ 'tvm__filter-btn--on': list.activeFilterChips.length }"
          :aria-label="$t('filter')"
          @click="list.filter = true"
        >
          <v-icon size="22" :color="list.activeFilterChips.length ? '#1565C0' : '#546E7A'">
            mdi-filter-outline
          </v-icon>
          <span v-if="list.activeFilterChips.length" class="tvm__filter-dot" />
        </button>
      </div>

      <!-- Summary strip -->
      <div class="tvm-sum">
        <div class="tvm-sum__col">
          <span>{{ $t("debtor") }}</span>
          <strong class="tvm-amount">{{ list.faNum($numberFormat(list.totals.debtor, 10)) }}</strong>
        </div>
        <div class="tvm-sum__col">
          <span>{{ $t("creditor") }}</span>
          <strong class="tvm-amount">{{ list.faNum($numberFormat(list.totals.creditor, 10)) }}</strong>
        </div>
        <div class="tvm-sum__col" :class="{ 'tvm-sum__col--neg': list.closingRemain < 0 }">
          <span>{{ $t("remain") }}</span>
          <strong class="tvm-amount">{{ list.faNum(list.signed(list.closingRemain)) }}</strong>
        </div>
      </div>
    </div>

    <div v-if="list.loading && !displayItems.length" class="tvm__state">
      <v-progress-circular indeterminate color="#5B7C99" size="28" />
    </div>
    <div v-else-if="!displayItems.length" class="tvm__state tvm__state--empty">
      <img class="tvm__empty-logo" src="@/assets/dashboard/no-data-cloud.png" alt="" />
      <p class="tvm__empty-text">{{ $t("no-data") }}</p>
    </div>
    <div v-else class="tvm__list">
      <div class="tvm__count">
        {{ list.faNum($numberFormat(list.totalItems || 0)) }} {{ $t("turnoverRows") }}
      </div>
      <article
        v-for="(item, index) in displayItems"
        :key="item.id || index"
        class="tvm-card"
        :class="{
          'tvm-card--deb': +list.amountOf(item, 'debtor') > 0,
          'tvm-card--cred': +list.amountOf(item, 'creditor') > 0,
        }"
        @click="openDetails(item)"
      >
        <div class="tvm-card__head">
          <div class="tvm-card__title-block">
            <span class="tvm-card__name">
              {{ $t("document") }} {{ list.faNum(item.head ? item.head.number : "") }}
            </span>
            <div class="tvm-card__sub tvm-card__sub--clamp">{{ list.contentText(item) || "—" }}</div>
          </div>
          <span class="tvm-card__date">
            {{ list.faNum(item.date ? item.date : item.head && item.head.date) }}
          </span>
        </div>
        <div v-if="item.given || item.fin4" class="tvm-card__accounts">
          <span v-if="item.given">{{ item.given.name }}</span>
          <span v-if="item.fin4">{{ item.fin4.name }}</span>
        </div>
        <div class="tvm-card__amounts">
          <div class="tvm-card__amount" :class="{ 'is-zero': !+list.amountOf(item, 'debtor') }">
            <span>{{ $t("debtor") }}</span>
            <b class="tvm-amount">{{ list.faNum($numberFormat(list.amountOf(item, "debtor"))) }}</b>
          </div>
          <div class="tvm-card__amount" :class="{ 'is-zero': !+list.amountOf(item, 'creditor') }">
            <span>{{ $t("creditor") }}</span>
            <b class="tvm-amount">{{ list.faNum($numberFormat(list.amountOf(item, "creditor"))) }}</b>
          </div>
          <div class="tvm-card__amount tvm-card__amount--remain" :class="{ 'is-neg': list.remainOf(item) < 0 }">
            <span>{{ $t("remain") }}</span>
            <b class="tvm-amount">{{ list.faNum(list.signed(list.remainOf(item))) }}</b>
          </div>
        </div>
      </article>
      <div v-if="appending" class="tvm__state tvm__state--more">
        <v-progress-circular indeterminate color="#5B7C99" size="22" />
      </div>
    </div>
    <div ref="sentinel" class="tvm__sentinel" />

    <!-- Details -->
    <v-bottom-sheet v-model="detailSheet" scrollable>
      <v-card class="tvm-sheet">
        <div class="tvm-sheet__handle" />
        <div class="tvm-sheet__header" dir="rtl">
          <span class="tvm-sheet__header-spacer" />
          <div class="tvm-sheet__title">{{ $t("details") }}</div>
          <button type="button" class="tvm-sheet__back" :aria-label="$t('back')" @click="detailSheet = false">
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div v-if="detailItem" class="tvm-sheet__quickbar" dir="rtl">
          <button type="button" class="tvm-sheet__quick" @click="run('view')">
            <v-icon size="18" color="#5B7C99">mdi-file-eye-outline</v-icon>
            {{ $t("document_view") }}
          </button>
          <button type="button" class="tvm-sheet__quick" @click="run('edit')">
            <v-icon size="18" color="#5B7C99">mdi-pencil-outline</v-icon>
            {{ $t("edit") }}
          </button>
          <button
            type="button"
            class="tvm-sheet__quick"
            v-clipboard:copy="list.docLink(detailItem)"
            @click="run('copied')"
          >
            <v-icon size="18" color="#5B7C99">mdi-link</v-icon>
            {{ $t("copy-link") }}
          </button>
          <button
            v-if="detailItem.reference && detailItem.reference.url"
            type="button"
            class="tvm-sheet__quick"
            @click="run('ref')"
          >
            <v-icon size="18" color="#5B7C99">mdi-link-variant</v-icon>
            {{ $t(detailItem.reference.type) }}
          </button>
        </div>
        <div v-if="detailItem" class="tvm-sheet__body" dir="rtl">
          <div class="tvm-sheet__card">
            <div v-for="(row, i) in detailRows(detailItem)" :key="i" class="tvm-sheet__row">
              <span class="tvm-sheet__row-label">{{ row.label }}</span>
              <span class="tvm-sheet__row-value" :class="{ 'tvm-amount': row.num }">{{ row.value }}</span>
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script>
export default {
  name: "TurnoverMobile",
  inject: ["turnoverList"],
  data: () => ({
    displayItems: [],
    appending: false,
    observer: null,
    detailSheet: false,
    detailItem: null,
  }),
  computed: {
    list() {
      return this.turnoverList;
    },
    currentPage() {
      return (this.list.tableOptions && this.list.tableOptions.page) || 1;
    },
  },
  watch: {
    "turnoverList.items"(val) {
      var items = val || [];
      if (this.appending) {
        var seen = {};
        this.displayItems.forEach(function (i) { seen[i.id] = true; });
        this.displayItems = this.displayItems.concat(items.filter(function (i) { return !seen[i.id]; }));
        this.appending = false;
      } else {
        this.displayItems = items.slice();
      }
    },
    "turnoverList.search"() {
      this.appending = false;
    },
  },
  // This screen has its own sticky bars; hide the app bottom navigation.
  activated() {
    this.$store.commit("SET_BOTTOMNAVIGATE", false);
  },
  deactivated() {
    this.$store.commit("SET_BOTTOMNAVIGATE", true);
  },
  mounted() {
    this.$store.commit("SET_BOTTOMNAVIGATE", false);
    this.displayItems = (this.list.items || []).slice();
    this.$nextTick(() => {
      if (!("IntersectionObserver" in window) || !this.$refs.sentinel) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0] && entries[0].isIntersecting) this.loadMore();
        },
        { rootMargin: "200px" }
      );
      this.observer.observe(this.$refs.sentinel);
    });
  },
  beforeDestroy() {
    this.$store.commit("SET_BOTTOMNAVIGATE", true);
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  },
  methods: {
    goBack() {
      if (window.history.length > 1) this.$router.back();
      else this.$router.push({ name: "Home" }).catch(() => {});
    },
    onRefreshTap() {
      this.appending = false;
      this.list.setFilter();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    loadMore() {
      var l = this.list;
      if (l.loading || this.appending) return;
      if (this.currentPage >= (l.pageCount || 1)) return;
      this.appending = true;
      l.tableOptions = Object.assign({}, l.tableOptions, { page: this.currentPage + 1 });
    },
    openDetails(item) {
      this.detailItem = item;
      this.detailSheet = true;
    },
    run(name) {
      var item = this.detailItem;
      this.detailSheet = false;
      if (name === "view") this.list.openDocView(item);
      else if (name === "edit") this.list.edit(item);
      else if (name === "copied") this.list.copied();
      else if (name === "ref") this.list.goToLink(item.reference.url);
    },
    detailRows(item) {
      var l = this.list;
      var rows = [];
      var push = function (label, value, num) {
        rows.push({ label: label, value: value == null || value === "" ? "—" : value, num: !!num });
      };
      push(this.$t("document-number"), l.faNum(item.head ? item.head.number : ""));
      push(this.$t("date"), l.faNum(item.date ? item.date : item.head && item.head.date));
      push(this.$t("content"), l.contentText(item));
      if (item.head && item.head.meta && item.head.meta.description) push(this.$t("description"), item.head.meta.description);
      push(this.$t("given"), item.given && item.given.name);
      push(this.$t("fin4"), item.fin4 && item.fin4.name);
      if (item.fin5) push(this.$t("fin5"), item.fin5.name);
      if (item.fin6) push(this.$t("fin6"), item.fin6.name);
      push(this.$t("debtor"), l.faNum(this.$numberFormat(l.amountOf(item, "debtor"))), true);
      push(this.$t("creditor"), l.faNum(this.$numberFormat(l.amountOf(item, "creditor"))), true);
      push(this.$t("remain"), l.faNum(l.signed(l.remainOf(item))) + " " + (l.statusLabel(item) || ""), true);
      if (item.currency) push(this.$t("currency"), item.currency.symbol || item.currency.name);
      var user = item.user && (item.user.name || [item.user.firstname, item.user.lastname].filter(Boolean).join(" "));
      push(this.$t("registerar"), user);
      return rows;
    },
  },
};
</script>

<style scoped>
.tvm {
  display: flex;
  flex-direction: column;
  padding: 0 12px 12px;
  background: #ffffff;
  min-height: calc(100vh - 52px);
  box-sizing: border-box;
  padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px));
}
.tvm--selecting {
  padding-bottom: calc(140px + env(safe-area-inset-bottom, 0px));
}
.tvm__header {
  position: sticky;
  top: 52px;
  z-index: 4;
  background: #ffffff;
  padding: 6px 0 10px;
  border-bottom: 1px solid #eef1f4;
  margin-bottom: 10px;
}
.tvm__bar {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 8px;
}
.tvm__bar-actions {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-width: 80px;
}
.tvm__bar-btn {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.tvm__bar-btn:active {
  background: rgba(91, 124, 153, 0.1);
}
.tvm__bar-btn--create {
  background: rgba(91, 124, 153, 0.1);
}
.tvm__bar-btn--back {
  margin-inline-start: auto;
}
.tvm__title {
  flex: 1 1 auto;
  margin: 0;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
}
.tvm__search-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.tvm__search {
  flex: 1 1 auto;
  min-width: 0;
  height: 44px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-radius: 6px;
  background: #f3f5f7;
}
.tvm__search-input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #37474f;
}
.tvm__search-clear {
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
}
.tvm__filter-btn {
  position: relative;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border-radius: 6px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  border: 1px solid #e0e4e8;
}
.tvm__filter-btn:active {
  background: rgba(91, 124, 153, 0.08);
}
.tvm__filter-btn--on {
  border-color: rgba(91, 124, 153, 0.45);
  background: rgba(91, 124, 153, 0.08);
}
.tvm__filter-dot {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5b7c99;
}
.tvm__select-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 6px 4px 0;
}
.tvm__select-bar-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 8px;
  border: 0;
  border-radius: 6px;
  background: rgba(91, 124, 153, 0.08);
  font-size: 11px;
  font-weight: 600;
  color: #455a64;
  cursor: pointer;
}
.tvm__select-bar-btn:active {
  background: rgba(91, 124, 153, 0.16);
}
.tvm__select-count {
  flex: 1 1 auto;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: #5b7c99;
}
.tvm__state {
  display: flex;
  justify-content: center;
  padding: 40px 0;
  color: #607d8b;
  font-size: 13px;
}
.tvm__state--empty {
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: calc(100vh - 220px);
  padding: 32px 16px 24px;
  background: #ffffff;
}
.tvm__state--more {
  padding: 14px 0;
}
.tvm__empty-logo {
  width: 72px;
  height: 72px;
  object-fit: contain;
  opacity: 0.85;
}
.tvm__empty-text {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #78909c;
  text-align: center;
}
.tvm__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 4px;
}
.tvm__sentinel {
  height: 1px;
}
.tvm-card {
  position: relative;
  border: 1px solid #e8edf2;
  border-radius: 6px;
  background: #f8fafc;
  padding: 12px;
  transition: transform 0.15s ease;
}
.tvm-card--selected {
  transform: scale(0.99);
  border-color: rgba(21, 101, 192, 0.45);
}
.tvm-card__select-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  border-radius: 6px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.tvm-card__select-overlay--on {
  background: rgba(38, 50, 56, 0.12);
}
.tvm-card__check {
  position: absolute;
  top: 8px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #0d47a1;
  box-shadow: 0 2px 6px rgba(13, 71, 161, 0.4);
}
.tvm-bulkbar {
  position: fixed;
  right: 12px;
  left: 12px;
  bottom: calc(72px + env(safe-area-inset-bottom, 0px));
  z-index: 6;
  display: flex;
  gap: 4px;
  padding: 8px;
  border: 1px solid #e8edf2;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: 0 8px 24px rgba(38, 50, 56, 0.14);
}
.tvm-bulkbar__btn {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-height: 48px;
  padding: 6px 2px;
  border: 0;
  border-radius: 6px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 10px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.tvm-bulkbar__btn:active {
  background: rgba(91, 124, 153, 0.15);
}
.tvm-bulkbar__btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.tvm-bulkbar__btn--danger {
  color: #e53935;
  background: rgba(229, 57, 53, 0.06);
}
.tvm-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}
.tvm-card__title-block {
  min-width: 0;
  flex: 1 1 auto;
}
.tvm-card__name-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.tvm-card__name-link:disabled {
  cursor: default;
}
.tvm-card__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: right;
}
.tvm-card__sub {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
}
.tvm-card__number {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #5b7c99;
  background: rgba(91, 124, 153, 0.1);
  border-radius: 6px;
  padding: 2px 8px;
}
.tvm-card__amounts {
  margin-top: 10px;
  border-top: 1px dashed rgba(144, 164, 174, 0.35);
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.tvm-card__amount-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.tvm-card__amount-label {
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.tvm-card__amount-value {
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
}
.tvm-card__status-pill {
  display: inline-flex;
  align-items: center;
  max-width: 62%;
  height: 24px;
  padding: 0 8px;
  border-radius: 6px;
  border: 1px solid transparent;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tvm-card__status-pill--empty {
  background: rgba(144, 164, 174, 0.12);
  color: #90a4ae;
  border-color: transparent;
}
.tvm-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
}
.tvm-card__actions {
  display: flex;
  gap: 6px;
  flex: 0 1 auto;
  min-width: 0;
  flex-wrap: wrap;
}
.tvm-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 34px;
  padding: 0 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  border: 0;
  -webkit-tap-highlight-color: transparent;
}
.tvm-btn:disabled {
  opacity: 0.45;
  cursor: default;
}
.tvm-btn--danger {
  border: 1px solid rgba(229, 57, 53, 0.35);
  background: rgba(229, 57, 53, 0.06);
  color: #c62828;
}
.tvm-btn--primary {
  background: #5b7c99;
  color: #ffffff;
}
.tvm-btn--outline {
  background: #ffffff;
  color: #5b7c99;
  border: 1px solid #cfd8dc;
}
.tvm-rel-btn {
  flex: 0 0 auto;
  height: 28px;
  padding: 0 8px;
  border: 0;
  border-radius: 6px;
  background: rgba(91, 124, 153, 0.08);
  color: #5b7c99;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.tvm-rel-btn:active {
  background: rgba(91, 124, 153, 0.16);
}
.tvm-rel-btn:disabled {
  background: transparent;
  color: #90a4ae;
  opacity: 0.55;
  cursor: default;
}
.tvm-rel-empty {
  padding: 18px 0;
  font-size: 12.5px;
  color: #90a4ae;
  text-align: center;
}
.tvm-rel-item {
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 10px;
}
.tvm-rel-item__title {
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
}
.tvm-rel-item__line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 28px;
}
.tvm-rel-item__key {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.tvm-rel-item__val {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
}
.tvm-sheet {
  border-radius: 6px 6px 0 0 !important;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
}
.tvm-sheet__handle {
  flex: 0 0 auto;
  width: 44px;
  height: 4px;
  border-radius: 6px;
  background: #cfd8dc;
  margin: 10px auto 2px;
}
.tvm-sheet__header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 10px;
  border-bottom: 1px solid #eef1f4;
}
.tvm-sheet__header-side {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  min-width: 0;
}
.tvm-sheet__header-side--end {
  justify-content: flex-end;
}
.tvm-sheet__header-spacer {
  flex: 0 0 40px;
  width: 40px;
}
.tvm-sheet__title {
  flex: 1 1 auto;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
}
.tvm-sheet__num-chip {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 6px;
  background: rgba(135, 158, 182, 0.14);
  border: 1px solid rgba(135, 158, 182, 0.28);
  color: #5b7c99;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.tvm-sheet__back {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.tvm-sheet__back:active {
  background: rgba(91, 124, 153, 0.1);
}
.tvm-sheet__quickbar {
  flex: 0 0 auto;
  display: flex;
  gap: 6px;
  padding: 8px 12px 0;
}
.tvm-sheet__quick {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  height: 46px;
  padding: 4px 2px;
  border: 1px solid #e8edf2;
  border-radius: 6px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 9px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}
.tvm-sheet__quick:disabled {
  opacity: 0.45;
  cursor: default;
}
.tvm-sheet__quick:active:not(:disabled) {
  background: rgba(91, 124, 153, 0.14);
}
.tvm-sheet__name-block {
  flex: 0 0 auto;
  background: #edf2f7;
  border-radius: 6px;
  padding: 10px 14px;
  margin: 6px 16px 0;
}
.tvm-sheet__name-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.tvm-sheet__name-value {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 70%;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tvm-sheet__name-block .tvm-sheet__linkrow {
  margin: 8px 0 0;
  background: rgba(255, 255, 255, 0.75);
}
.tvm-sheet__body {
  overflow-y: auto;
  padding: 8px 16px 20px;
}
.tvm-sheet__card {
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 6px;
  padding: 4px 14px;
}
.tvm-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #e8edf2;
}
.tvm-sheet__card .tvm-sheet__row:last-child {
  border-bottom: 0;
}
.tvm-sheet__row--band {
  margin: 0 -14px;
  padding: 8px 14px;
  border-bottom-color: rgba(91, 124, 153, 0.12);
  background: rgba(91, 124, 153, 0.05);
}
.tvm-sheet__row-label {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.tvm-sheet__row-value {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
}
.tvm-sheet__tags {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-end;
  max-width: 62%;
}
.tvm-sheet__linkrow {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 40px;
  margin: 6px 0 2px;
  padding: 0 10px;
  border: 1px solid rgba(0, 150, 136, 0.16);
  border-radius: 6px;
  background: rgba(0, 150, 136, 0.07);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.tvm-sheet__linkrow-text {
  flex: 1 1 auto;
  font-size: 12.5px;
  font-weight: 700;
  color: #00796b;
  text-align: right;
}
.tvm-sheet__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.tvm-sheet__actions--sheet {
  padding-top: 4px;
}
.tvm-sheet__action {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 42px;
  padding: 0 6px;
  border: 0;
  border-bottom: 1px solid #e8edf2;
  border-radius: 6px;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.tvm-sheet__action:disabled {
  opacity: 0.45;
  cursor: default;
}
.tvm-sheet__action:active:not(:disabled) {
  background: rgba(91, 124, 153, 0.08);
}
.tvm-sheet__action--danger {
  color: #c62828;
}
.tvm-sheet__action--log {
  cursor: default;
  padding: 0;
}
.tvm-sheet__action--log > div {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
}
.tvm-sheet__action--log ::v-deep .v-list-item {
  flex: 1 1 auto;
  min-height: 42px;
  height: 42px;
  padding: 0;
}
.tvm-sheet__action--log ::v-deep .v-list-item__icon {
  margin: 0 !important;
  min-width: 19px;
  align-self: center;
}
.tvm-sheet__action--log ::v-deep .v-list-item__icon .v-icon {
  font-size: 19px !important;
  color: #5b7c99 !important;
}
.tvm-sheet__action--log ::v-deep .v-list-item__title {
  margin-inline-start: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  text-align: right;
}
.tvm-sheet--filter {
  max-height: 92vh;
  border-radius: 6px 6px 0 0 !important;
}
.tvm-sheet__footer {
  flex: 0 0 auto;
  display: flex;
  gap: 8px;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #eef1f4;
  background: #ffffff;
}
.tvm-sheet__btn {
  flex: 1 1 0;
  height: 44px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.tvm-sheet__btn--primary {
  border: 0;
  background: #5b7c99;
  color: #ffffff;
}
.tvm-sheet__btn--primary:active {
  background: #4d6a84;
}
.tvm-sheet__btn--ghost {
  border: 1px solid #cfd8dc;
  background: #ffffff;
  color: #546e7a;
}
.tvm-sheet__btn--ghost:active {
  background: rgba(91, 124, 153, 0.08);
}
.tvm-filter-nav {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0;
  background: #ffffff;
}
.tvm-filter-nav__item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 12px;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: #fff;
  text-align: right;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.tvm-filter-nav__item:active {
  background: #f7f9fc;
}
.tvm-filter-nav__item:last-child {
  border-bottom: 0;
}
.tvm-filter-nav__icon {
  flex: 0 0 auto;
}
.tvm-filter-nav__texts {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;
}
.tvm-filter-nav__item-title {
  display: block;
  width: 100%;
  font-size: 14px;
  font-weight: 700;
  color: #212121;
  line-height: 1.35;
  text-align: right;
}
.tvm-filter-nav__item-sub {
  display: block;
  width: 100%;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 400;
  color: #90a4ae;
  line-height: 1.35;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tvm-filter-nav__dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5b7c99;
}
.tvm-filter-fields {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 14px 16px 6px;
  background: #ffffff;
}
.tvm-filter-fields > * {
  margin-bottom: 14px;
}
.tvm-picker-host {
  display: none !important;
}
.tvm-picker-overlay {
  position: relative;
  height: 40px;
  overflow: hidden;
  border-radius: 6px;
}
.tvm-picker-cover {
  position: absolute !important;
  inset: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
}
.tvm-picker-cover__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
}
.tvm-f-field-wrap {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.tvm-f-field-wrap__label {
  display: block;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 600;
  color: #455a64;
  text-align: right;
  line-height: 1.4;
  margin: 0 2px 4px;
}
.tvm-f-picker-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  min-height: 40px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid #cfd8dc;
  border-radius: 6px;
  background: #fff;
  box-sizing: border-box;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.tvm-f-picker-field:active {
  background: #eef4f8;
  border-color: #90caf9;
}
.tvm-f-picker-field__text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  color: #263238;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tvm-select-layer {
  padding: 6px 16px 20px;
}
.tvm-select-layer__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 48px;
  padding: 10px 4px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: transparent;
  text-align: right;
  cursor: pointer;
}
.tvm-select-layer__text {
  font-size: 13.5px;
  font-weight: 500;
  color: #37474f;
}
.tvm-select-layer__clear {
  display: block;
  width: 100%;
  padding: 14px 4px 4px;
  border: 0;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #c62828;
  text-align: center;
  cursor: pointer;
}
.tvm-mobile-date {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 16px;
  background: #ffffff;
}
.tvm-mobile-date__selected {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #e0e6ed;
  border-radius: 6px;
  background: #f7f9fc;
}
.tvm-mobile-date__label {
  width: 100%;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.tvm-mobile-date__value {
  font-size: 18px;
  font-weight: 800;
  color: #263238;
  letter-spacing: 0.02em;
}
.tvm-mobile-date__clear {
  border: 0;
  background: transparent;
  color: #e91e63;
  font-size: 13px;
  font-weight: 700;
  padding: 4px 0;
  cursor: pointer;
}
.tvm-mobile-date__picker {
  width: 100%;
}
.tvm-mobile-date__picker ::v-deep .vpd-main,
.tvm-mobile-date__picker ::v-deep .vpd-wrapper,
.tvm-mobile-date__picker ::v-deep .vpd-container,
.tvm-mobile-date__picker ::v-deep .vpd-content {
  width: 100% !important;
  max-width: 100% !important;
}
.tvm-confirm-sheet {
  border-radius: 6px 6px 0 0 !important;
  padding: 8px 16px 16px;
  background: #fff !important;
}
.tvm-confirm-sheet__handle {
  width: 40px;
  height: 4px;
  margin: 4px auto 14px;
  border-radius: 6px;
  background: #cfd8dc;
}
.tvm-confirm-sheet__icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 10px;
  border-radius: 50%;
  background: rgba(229, 57, 53, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}
.tvm-confirm-sheet__title {
  text-align: center;
  font-size: 16px;
  font-weight: 800;
  color: #263238;
  margin-bottom: 4px;
}
.tvm-confirm-sheet__subtitle {
  text-align: center;
  font-size: 13px;
  font-weight: 500;
  color: #78909c;
  margin: 0 0 14px;
}
.tvm-confirm-sheet__rows {
  border: 1px solid #e8edf2;
  border-radius: 6px;
  background: #f8fafc;
  padding: 4px 12px;
  margin-bottom: 14px;
}
.tvm-confirm-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #e8edf2;
  font-size: 13px;
  color: #546e7a;
}
.tvm-confirm-sheet__row:last-child {
  border-bottom: 0;
}
.tvm-confirm-sheet__row strong {
  color: #263238;
  font-weight: 700;
  text-align: left;
}
.tvm-confirm-sheet__items {
  max-height: 160px;
  overflow-y: auto;
  padding: 4px 0 8px;
}
.tvm-confirm-sheet__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px dashed #e0e4e8;
}
.tvm-confirm-sheet__item:last-child {
  border-bottom: 0;
}
.tvm-confirm-sheet__item-main {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.tvm-confirm-sheet__item-index {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(91, 124, 153, 0.12);
  color: #5b7c99;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.tvm-confirm-sheet__item-name {
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tvm-confirm-sheet__item-number {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
}
.tvm-confirm-sheet__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.tvm-confirm-sheet__yes {
  border-radius: 6px !important;
}
.tvm-confirm-sheet__no {
  border-radius: 6px !important;
}

/* Accounting-specific additions */
.tvm__count {
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
  padding: 2px 4px 0;
}
.tvm-card__head-side {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}
.tvm-card__sub--clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
}
.tvm-doc-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
  color: #b26a00;
  background: rgba(255, 179, 0, 0.14);
}
.tvm-doc-pill--always {
  color: #2e7d32;
  background: rgba(46, 125, 50, 0.12);
}
.tvm-amount,
.tvm-ltr {
  direction: ltr;
  font-variant-numeric: tabular-nums;
}
.tvm-sum {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 12px;
  padding: 12px;
  border-radius: 6px;
  background: rgba(91, 124, 153, 0.08);
}
.tvm-sum--warn {
  background: rgba(255, 179, 0, 0.12);
}
.tvm-sum__col {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.tvm-sum__col span {
  font-size: 11px;
  font-weight: 600;
  color: #78909c;
}
.tvm-sum__col strong {
  font-size: 13px;
  color: #263238;
  direction: ltr;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tvm-f-pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 10px;
}
.tvm-f-input {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #cfd8dc;
  border-radius: 6px;
  background: #fff;
  box-sizing: border-box;
  outline: none;
  direction: ltr;
  text-align: right;
  font-size: 14px;
  color: #37474f;
}
.tvm-f-input:focus {
  border-color: #5b7c99;
}

/* Turnover-specific */
.tvm__title small {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #78909c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tvm-sum {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-top: 8px;
}
.tvm-sum__col {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding: 7px 9px;
  border-radius: 6px;
  background: #f3f5f7;
}
.tvm-sum__col span {
  font-size: 11px;
  font-weight: 600;
  color: #78909c;
}
.tvm-sum__col strong {
  font-size: 12.5px;
  color: #263238;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tvm-sum__col--neg strong {
  color: #c62828;
}
.tvm-amount {
  direction: ltr;
  unicode-bidi: isolate;
  font-variant-numeric: tabular-nums;
}
.tvm-card {
  cursor: pointer;
  border-inline-start: 3px solid transparent;
}
.tvm-card--deb {
  border-inline-start-color: #2e7d32;
}
.tvm-card--cred {
  border-inline-start-color: #c2185b;
}
.tvm-card__date {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #5b7c99;
  background: rgba(91, 124, 153, 0.1);
  border-radius: 6px;
  padding: 2px 8px;
}
.tvm-card__sub--clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
}
.tvm-card__accounts {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}
.tvm-card__accounts span {
  font-size: 11px;
  font-weight: 600;
  color: #546e7a;
  background: #f3f5f7;
  border-radius: 6px;
  padding: 2px 7px;
}
.tvm-card__amounts {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px dashed rgba(144, 164, 174, 0.35);
}
.tvm-card__amount {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.tvm-card__amount span {
  font-size: 11px;
  font-weight: 600;
  color: #78909c;
}
.tvm-card__amount b {
  font-size: 12.5px;
  color: #263238;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tvm-card__amount.is-zero b {
  color: #b0bec5;
  font-weight: 500;
}
.tvm-card__amount--remain b {
  color: #1565c0;
}
.tvm-card__amount--remain.is-neg b {
  color: #c62828;
}
.tvm__count {
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
  padding: 2px 4px 0;
}
</style>
